import React, { useState, useEffect } from "react";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { saveAs } from "file-saver";
import { fetchADLRecordReport } from "../../../store/slices/reportSlice";
import { fetchPatients } from "../../../store/slices/patientsSlice";
import Breadcrumbs from "../../../components/common/Breadcrumbs";
import MainLayout from "../../../components/layout/MainLayout";
import logo from "../../../assets/images/logo-samplee.png";

const selectStyles = {
  control: (base) => ({ ...base, minHeight: "36px", fontSize: "0.85rem", borderRadius: "0.375rem", borderColor: "#d9dee3" }),
  menu: (base) => ({ ...base, fontSize: "0.85rem", zIndex: 9999 }),
  option: (base, state) => ({ ...base, backgroundColor: state.isSelected ? "#666ee8" : state.isFocused ? "#f5f5f9" : "white", color: state.isSelected ? "white" : "#566a7f", cursor: "pointer" }),
};

const val = (v) => (v === null || v === undefined || v === "" ? "—" : v);
const shortDate = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  return `${String(dt.getDate()).padStart(2, "0")}/${String(dt.getMonth() + 1).padStart(2, "0")}/${dt.getFullYear()}`;
};
const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const scoreCell = (s) => {
  if (s === null || s === undefined) return <span className="text-muted">–</span>;
  const cls = s === 1 ? "bg-label-success" : s === 2 ? "bg-label-warning" : "bg-label-danger";
  return <span className={`badge ${cls}`} style={{ minWidth: "26px" }}>{s}</span>;
};

/* ---- Print/PDF stylesheet (scoped to #adl-print-root, mirrors ADLsDailyRecordFormPrint) ---- */
const PRINT_CSS = `
#adl-print-root * { box-sizing: border-box; }
#adl-print-root { font-family: Arial, sans-serif; line-height: 1.4; color: #000; background:#fff; font-size: 9pt; }
#adl-print-root .print-container { display:block; width:100%; padding:20px; margin:0 auto; position:relative; background:#fff; }
#adl-print-root .logo-container { display:flex; justify-content:flex-start; margin-bottom:10px; }
#adl-print-root .logo-container img { height:50px; width:auto; }
#adl-print-root .header h3 { font-size:1.4em; font-weight:bold; text-align:center; border-bottom:1.5px solid #383838; padding-bottom:10px; margin:0 0 8px; }
#adl-print-root .header h6 { font-size:1.1em; text-align:center; margin:0 0 15px; font-weight:600; }
#adl-print-root .info-row { display:flex; justify-content:space-between; margin-bottom:14px; font-size:9.5pt; }
#adl-print-root .underline { display:inline-block; border-bottom:1px solid #000; margin-left:5px; padding-right:10px; font-weight:bold; }
#adl-print-root .instruction { margin:12px 0; font-size:9pt; border:1px solid #ddd; padding:8px; background:#f9f9f9; }
#adl-print-root .instruction p { font-weight:bold; margin:0 0 4px; }
#adl-print-root .instruction ul { list-style:none; padding-left:0; margin:0; display:grid; grid-template-columns:1fr 1fr 1fr; gap:5px; }
#adl-print-root .main-table { width:100%; border-collapse:collapse; margin-top:15px; font-size:8.5pt; }
#adl-print-root .main-table th, #adl-print-root .main-table td { border:0.75pt solid #000; padding:3px 2px; text-align:center; vertical-align:middle; }
#adl-print-root .main-table th { background:#d0cece; font-weight:bold; }
#adl-print-root .activity-name { text-align:left; font-weight:bold; padding-left:6px; }
#adl-print-root .score-stack { display:flex; flex-direction:column; align-items:center; gap:2px; }
#adl-print-root .score-line { display:flex; align-items:center; gap:3px; }
#adl-print-root .checkbox { display:inline-block; width:12px; height:12px; border:1px solid #000; line-height:10px; font-size:10px; font-weight:bold; text-align:center; }
#adl-print-root .checkbox.checked::before { content:"✓"; }
#adl-print-root .footer { margin-top:25px; border-top:1.5px solid #000; padding-top:8px; font-size:8.5pt; }
#adl-print-root .footer p { text-align:center; margin:0 0 4px; }
#adl-print-root .footer-sign { display:flex; justify-content:space-between; margin-top:10px; font-size:9pt; }
`;

const ADLRecordReport = () => {
  const dispatch = useDispatch();
  const { adlRecordReport: report, isLoading, error } = useSelector((s) => s.reports);
  const { patients } = useSelector((s) => s.patients);

  const [patientId, setPatientId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [logoData, setLogoData] = useState(logo);
  const [busy, setBusy] = useState(false);

  useEffect(() => { dispatch(fetchPatients()); }, [dispatch]);
  useEffect(() => {
    if (patientId) dispatch(fetchADLRecordReport({ patientId, dateFrom, dateTo }));
  }, [dispatch, patientId, dateFrom, dateTo]);

  // Convert the logo to a data URL so it embeds cleanly in PDF / Word / new print window.
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      try {
        const c = document.createElement("canvas");
        c.width = img.naturalWidth; c.height = img.naturalHeight;
        c.getContext("2d").drawImage(img, 0, 0);
        setLogoData(c.toDataURL("image/png"));
      } catch (e) { /* fall back to url */ }
    };
    img.src = logo;
  }, []);

  const patientOptions = (patients || []).map((p) => ({
    value: p.id,
    label: `${p.first_name || ""} ${p.last_name || ""} (${p.mr_no || "N/A"})`.trim(),
  }));

  const info = report?.info || {};
  const tables = report?.tables || [];
  const fileBase = `ADL_Record_Report_${(info.client_name || "client").replace(/\s+/g, "_")}`;

  /* ---- Build the print HTML body (one styled container per 14-day chunk) ---- */
  const buildPrintBody = () => {
    const containers = tables.map((tbl, idx) => {
      const isLast = idx === tables.length - 1;
      const dates = tbl.dates || [];
      const range = dates.length ? `${shortDate(dates[0])} till ${shortDate(dates[dates.length - 1])}` : "No records";

      const headCols = dates.map((d) => `<th style="font-size:8pt">${shortDate(d)}</th>`).join("");
      const bodyRows = (tbl.rows || []).map((r) => {
        const cells = dates.map((d) => {
          const s = r.scores[d];
          const box = (n) => `<div class="score-line"><span>${n}</span><span class="checkbox ${s === n ? "checked" : ""}"></span></div>`;
          return `<td><div class="score-stack">${box(1)}${box(2)}${box(3)}</div></td>`;
        }).join("");
        return `<tr><td class="activity-name">${esc(r.activity)}</td>${cells}<td>${r.total}</td><td style="font-size:8pt">${esc(r.staff === "—" ? "" : r.staff)}</td></tr>`;
      }).join("");

      return `
        <div class="print-container" style="${!isLast ? "page-break-after:always;" : ""}">
          <div class="logo-container"><img src="${logoData}" alt="The Recovery House" /></div>
          <div class="header"><h3>THE RECOVERY HOUSE</h3><h6>ADL RECORD SHEET</h6></div>
          <div class="info-row">
            <div><span>Client Name:</span><span class="underline">${esc(info.client_name)}</span></div>
            <div><span>Room No.:</span><span class="underline">${esc(info.room_number)}</span></div>
            <div><span>Date Range:</span><span class="underline">${range}</span></div>
          </div>
          <div class="instruction">
            <p>Instruction: Scoring System</p>
            <ul>
              <li><strong>1 = Independent</strong> (performs the activity independently)</li>
              <li><strong>2 = Supervision</strong> (performs the activity with supervision)</li>
              <li><strong>3 = Totally Dependent</strong> (requires assistance to perform)</li>
            </ul>
          </div>
          <table class="main-table">
            <thead>
              <tr><th style="width:16%">Activity</th>${headCols}<th style="width:10%">Total Score (Out of 135)</th><th style="width:14%">Staff Name</th></tr>
            </thead>
            <tbody>${bodyRows}</tbody>
          </table>
          <div class="footer">
            <p>156 Bangalore Town, Tipu Sultan Road Karachi-Pakistan Ph : 021-34546364-65-66</p>
            <p>A Project of <strong>&ldquo;CARVAN OF LIFE PAKISTAN TRUST&rdquo;</strong></p>
            <div class="footer-sign">
              <div><strong>Checked by:</strong> <span class="underline">${esc(info.checked_by === "—" ? "" : info.checked_by)}</span></div>
              <div><strong>Date:</strong> <span class="underline">${esc(info.report_date || "")}</span></div>
            </div>
            <div class="footer-sign" style="justify-content:flex-end;margin-top:10px">
              <div><strong>Nursing Manager / Head Nurse:</strong> <span class="underline" style="min-width:180px">${esc(info.nursing_manager === "—" ? "" : info.nursing_manager)}</span></div>
            </div>
          </div>
        </div>`;
    }).join("");

    return `<div id="adl-print-root">${containers}</div>`;
  };

  /* ---- PRINT: open a clean window styled exactly like the ADL form print ---- */
  const handlePrint = () => {
    const w = window.open("", "_blank", "width=1100,height=800");
    if (!w) return;
    w.document.write(`<!DOCTYPE html><html><head><title>ADL Record Report</title><style>${PRINT_CSS}</style></head><body>${buildPrintBody()}</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); }, 500);
  };

  /* ---- EXPORT PDF: render the styled print HTML to a landscape PDF (page per chunk) ---- */
  const exportPDF = async () => {
    setBusy(true);
    const holder = document.createElement("div");
    holder.style.cssText = "position:fixed;left:-10000px;top:0;width:1100px;background:#fff;";
    holder.innerHTML = `<style>${PRINT_CSS}</style>${buildPrintBody()}`;
    document.body.appendChild(holder);
    try {
      const containers = holder.querySelectorAll(".print-container");
      const pdf = new jsPDF("l", "mm", "a4");
      const pw = pdf.internal.pageSize.getWidth();
      const ph = pdf.internal.pageSize.getHeight();
      for (let i = 0; i < containers.length; i++) {
        const canvas = await html2canvas(containers[i], { scale: 2, backgroundColor: "#ffffff", useCORS: true });
        const imgData = canvas.toDataURL("image/png");
        const margin = 6;
        let w = pw - margin * 2;
        let h = (canvas.height * w) / canvas.width;
        if (h > ph - margin * 2) { h = ph - margin * 2; w = (canvas.width * h) / canvas.height; }
        if (i > 0) pdf.addPage();
        pdf.addImage(imgData, "PNG", (pw - w) / 2, margin, w, h);
      }
      pdf.save(`${fileBase}.pdf`);
    } finally {
      document.body.removeChild(holder);
      setBusy(false);
    }
  };

  /* ---- EXPORT WORD: HTML .doc with the TRH logo on top ---- */
  const exportDocx = () => {
    const cell = (c, bold) => `<td style="border:1px solid #444;padding:3px;text-align:center;font-size:10px;${bold ? "font-weight:bold;background:#eef;text-align:left;" : ""}">${c}</td>`;
    let body = "";
    tables.forEach((tbl, idx) => {
      const dates = tbl.dates || [];
      const range = dates.length ? `${shortDate(dates[0])} till ${shortDate(dates[dates.length - 1])}` : "";
      body += `<h4 style="margin-bottom:4px">Table ${idx + 1} &nbsp;<span style="font-weight:normal;font-size:11px">(${range})</span></h4>`;
      body += `<table style="border-collapse:collapse;width:100%"><tr>${cell("Activity", true)}${dates.map((d) => cell(shortDate(d), true)).join("")}${cell("Total Score (Out of 135)", true)}${cell("Staff Name", true)}</tr>`;
      (tbl.rows || []).forEach((r) => {
        body += `<tr>${cell(esc(r.activity), true)}${dates.map((d) => cell(r.scores[d] ?? "–")).join("")}${cell(r.total)}${cell(esc(r.staff === "—" ? "" : r.staff))}</tr>`;
      });
      body += `</table><br/>`;
    });
    const html = `
      <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word'><head><meta charset='utf-8'></head><body>
      <div style="text-align:center"><img src="${logoData}" style="height:60px" /></div>
      <h2 style="text-align:center;margin:6px 0">THE RECOVERY HOUSE</h2>
      <h3 style="text-align:center;margin:0 0 10px">Activities of Daily Living (ADL's Record)</h3>
      <p><b>Client Name:</b> ${esc(info.client_name)} &nbsp; <b>Room Number:</b> ${esc(info.room_number)} &nbsp; <b>Date Range:</b> ${esc(info.date_range)}</p>
      <p><b>Scoring:</b> 1 = Independent, 2 = Supervision, 3 = Totally Dependent</p>
      ${body}
      <p><b>Checked by:</b> ${esc(info.checked_by === "—" ? "" : info.checked_by)} &nbsp;&nbsp; <b>Nursing Manager / Head Nurse:</b> ${esc(info.nursing_manager === "—" ? "" : info.nursing_manager)}</p>
      </body></html>`;
    saveAs(new Blob(["﻿", html], { type: "application/msword" }), `${fileBase}.doc`);
  };

  const canExport = report && report.hasData;

  return (
    <MainLayout>
      <Breadcrumbs />

      {/* Header + filters */}
      <div className="card shadow-sm mb-3">
        <div className="card-body" style={{ background: "linear-gradient(135deg, #f5f7ff 0%, #eef2ff 100%)" }}>
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
            <div>
              <h4 className="mb-1 text-dark fw-bold">ADL Record Report</h4>
              <div className="text-muted small">Activities of Daily Living — scored daily, grouped into 14-day (2-week) tables</div>
            </div>
            <div className="d-flex gap-2 flex-wrap align-items-end">
              <div style={{ minWidth: "230px" }}>
                <label className="form-label small fw-bold mb-1">Select Client</label>
                <Select
                  options={patientOptions}
                  styles={selectStyles}
                  value={patientOptions.find((o) => o.value === patientId) || null}
                  onChange={(o) => setPatientId(o ? o.value : "")}
                  placeholder="Search client..."
                  isSearchable isClearable
                />
              </div>
              <div>
                <label className="form-label small fw-bold mb-1">Date From</label>
                <input type="date" className="form-control form-control-sm" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={{ minHeight: "36px" }} />
              </div>
              <div>
                <label className="form-label small fw-bold mb-1">Date To</label>
                <input type="date" className="form-control form-control-sm" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={{ minHeight: "36px" }} />
              </div>
            </div>
          </div>

          {canExport && (
            <div className="mt-3 border-top pt-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div className="d-flex align-items-center gap-2">
                <div className="dropdown">
                  <button className="btn btn-label-primary btn-sm dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
                    <i className="ri-download-2-line me-1"></i> Export
                  </button>
                  <ul className="dropdown-menu shadow-sm">
                    <li>
                      <button className="dropdown-item" onClick={exportPDF} disabled={busy}>
                        <i className="ri-file-pdf-line me-1 text-danger"></i> PDF
                      </button>
                    </li>
                    <li>
                      <button className="dropdown-item" onClick={exportDocx}>
                        <i className="ri-file-word-line me-1 text-primary"></i> Word
                      </button>
                    </li>
                    <li>
                      <button className="dropdown-item" onClick={handlePrint}>
                        <i className="ri-printer-line me-1 text-secondary"></i> Print
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
              <span className="small text-muted">Total recorded days: <strong>{report.totalDates}</strong> · Tables: <strong>{tables.length}</strong></span>
            </div>
          )}
        </div>
      </div>

      {/* Legend */}
      {canExport && (
        <div className="card shadow-sm mb-3"><div className="card-body py-2 d-flex flex-wrap gap-3 align-items-center small">
          <span className="fw-bold">Scoring System:</span>
          <span><span className="badge bg-label-success me-1">1</span> Independent</span>
          <span><span className="badge bg-label-warning me-1">2</span> Supervision</span>
          <span><span className="badge bg-label-danger me-1">3</span> Totally Dependent</span>
        </div></div>
      )}

      {!patientId ? (
        <div className="card shadow-sm"><div className="card-body text-center text-muted py-5">
          <i className="ri-list-check-2 fs-1 d-block mb-2"></i>
          Please select a client to generate the ADL Record Report.
        </div></div>
      ) : isLoading ? (
        <div className="card shadow-sm"><div className="card-body text-center py-5">
          <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
        </div></div>
      ) : error ? (
        <div className="alert alert-danger">{error}</div>
      ) : report && !report.hasData ? (
        <div className="card shadow-sm"><div className="card-body text-center text-muted py-5">
          <i className="ri-information-line fs-1 d-block mb-2"></i>
          No ADL records found for this client{(dateFrom || dateTo) ? " in the selected date range" : ""}.
        </div></div>
      ) : report ? (
        <div id="adl-report-print">
          {/* Client Info */}
          <div className="card shadow-sm mb-3">
            <div className="card-header bg-label-primary py-2"><h6 className="mb-0 fw-bold">Client Information</h6></div>
            <div className="card-body p-0">
              <table className="table table-bordered mb-0">
                <tbody>
                  <tr><th style={{ width: "20%" }} className="bg-light">Client Name</th><td>{val(info.client_name)}</td><th style={{ width: "20%" }} className="bg-light">Room Number</th><td>{val(info.room_number)}</td></tr>
                  <tr><th className="bg-light">Date Range</th><td>{val(info.date_range)}</td><th className="bg-light">Checked By</th><td>{val(info.checked_by)}</td></tr>
                  <tr><th className="bg-light">Nursing Manager / Head Nurse</th><td colSpan={3}>{val(info.nursing_manager)}</td></tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* One card per 14-day table */}
          {tables.map((tbl, idx) => (
            <div className="card shadow-sm mb-3" key={idx}>
              <div className="card-header bg-label-primary py-2 d-flex justify-content-between align-items-center">
                <h6 className="mb-0 fw-bold">Table {idx + 1}</h6>
                <span className="small text-muted">{shortDate(tbl.dates[0])} – {shortDate(tbl.dates[tbl.dates.length - 1])} ({tbl.dates.length} day{tbl.dates.length > 1 ? "s" : ""})</span>
              </div>
              <div className="card-body p-0 table-responsive">
                <table className="table table-bordered table-sm mb-0 text-center align-middle" style={{ fontSize: "0.8rem" }}>
                  <thead className="table-light">
                    <tr>
                      <th className="text-start" style={{ minWidth: "130px", position: "sticky", left: 0, background: "#f5f5f9" }}>Activity</th>
                      {tbl.dates.map((d) => (<th key={d} style={{ minWidth: "42px" }}>{shortDate(d)}</th>))}
                      <th style={{ minWidth: "70px" }}>Total<br /><span className="small text-muted">(/135)</span></th>
                      <th style={{ minWidth: "90px" }}>Staff Name</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tbl.rows.map((r) => (
                      <tr key={r.activity}>
                        <td className="text-start fw-semibold" style={{ position: "sticky", left: 0, background: "#fff" }}>{r.activity}</td>
                        {tbl.dates.map((d) => (<td key={d}>{scoreCell(r.scores[d])}</td>))}
                        <td className="fw-bold">{r.total}</td>
                        <td className="small">{val(r.staff)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </MainLayout>
  );
};

export default ADLRecordReport;
